import { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

import { LessonPlanAPIs } from "../api";
import { groupLessonPlans } from "../utils";

import { GlobalContext } from "../../../context/GlobalState";

export default function useGroupPlan() {
  const { userDetails } = useContext(GlobalContext);
  const [loading, setLoading] = useState(false);
  const [lessonPlans, setLessonPlans] = useState([]);
  const [columns, setColumns] = useState([]);

  async function fetchLessonPlan(query) {
    try {
      if (loading) return;
      setLoading(true);
      if (!query?.teacherId || !query.startDate) {
        return new Error("teacherId and startDate are required");
      }

      // Build day range from startDate query
      const day = new Date(query.startDate);
      if (isNaN(day)) {
        throw new Error("Invalid startDate format");
      }

      const startOfDay = new Date(day.setHours(0, 0, 0, 0));
      const endOfDay = new Date(day.setHours(23, 59, 59, 999));
      axios
        .get(
          LessonPlanAPIs.fetchGroupedPlans(
            userDetails.userId,
            new URLSearchParams({
              teacherId: query?.teacherId,
              "startDate[gte]": startOfDay,
              "startDate[lte]": endOfDay,
            }).toString()
          ),
          {
            headers: {
              token: localStorage.getItem("authToken"),
            },
            validateStatus: () => true,
          }
        )
        .then((res) => {
          if (res.data.status !== "success") throw res.data;
          const plans = res.data?.data?.plans || [];
          setLessonPlans(plans);
        })
        .catch((err) => {
          console.log(err.message);
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (err) {
      alert(err.message);
      setLoading(false);
    }
  }

  const info = useMemo(() => {
    const plan = lessonPlans[0];
    return {
      classesTeaching: plan?.classesTeaching,
      level: plan?.level,
      startDate: plan?.startDate,
      teacherId: plan?.teacherId?._id,
      teacherName: plan?.teacherId?.name,
      schoolName: userDetails?.schoolId?.name,
      role: userDetails?.role,
    };
  }, [lessonPlans, userDetails]);

  const planIds = useMemo(() => {
    return lessonPlans.map((plan) => plan._id);
  }, [lessonPlans]);

  useEffect(() => {
    const groupedPlans = groupLessonPlans(lessonPlans);
    setColumns(groupedPlans);
  }, [lessonPlans]);

  /**Cell sync function */
  function syncCell(newColumn, colId) {
    const columnIndex = columns.findIndex(
      (column) => column.colName === newColumn.colName
    );
    if (columnIndex < 0) return;
    const column = columns[columnIndex];
    const cellIndex = column.cells.findIndex((cell) => {
      return !colId
        ? cell._id === newColumn._id
        : cell.tempId === newColumn.tempId;
    });
    if (cellIndex < 0) return;
    const cell = column.cells[cellIndex];
    if (cell.new) {
      delete newColumn.tempId;
      delete newColumn.new;
    }
    column.cells[cellIndex] = {
      ...cell,
      ...newColumn,
      colId,
    };
    columns[columnIndex] = column;
    setColumns([...columns]);
  }

  function syncColumn(col, updates) {
    const columnIndex = columns.findIndex(
      (column) =>
        column.colName === col.colName && col.colIndex === column.colIndex
    );
    if (columnIndex < 0) return;
    columns[columnIndex] = {
      ...columns[columnIndex],
      ...updates,
    };
    setColumns([...columns]);
  }

  function syncRemovedColumn(col) {
    const newColumns = columns.filter(
      (column) =>
        column.colName !== col.colName && col.colIndex !== column.colIndex
    );
    setColumns([...newColumns]);
  }

  return {
    loading,
    lessonPlans,
    columns,
    info,
    planIds,
    setColumns,
    fetchLessonPlan,
    syncCell,
    syncColumn,
    syncRemovedColumn,
  };
}
